import { useTranslations } from 'next-intl';
import { useCallback, useRef } from 'react';
import { toast } from '@/app/components/ui/use-toast';
import {
  ACCEPT_IMAGE_MIMETYPE,
  ACCEPT_VIDEO_MIMETYPE,
  MAX_IMAGE_FILE_SIZE,
  MAX_VIDEO_FILE_SIZE,
  UPLOAD_SCENE,
  type UploadScene,
} from '@/app/constants';
import {
  ACCEPT_AUDIO_MIMETYPE,
  type AudioUploadFileInfo,
  useAudioUpload,
  useFileUpload,
  useImageValidator,
  useVideoUpload,
} from '@/app/hooks';
import type { AudioUploadFile, ImageUploadFileInfo, UploadFile, VideoUploadFileInfo } from '@/app/types';

/**
 * 判断文件类型是否为图片
 */
const isImageFile = (file: File): boolean => {
  return ACCEPT_IMAGE_MIMETYPE.includes(file.type);
};

/**
 * 判断文件类型是否为视频
 */
const isVideoFile = (file: File, videoAccept: string[]): boolean => {
  return videoAccept.includes(file.type) ?? false;
};

/**
 * 判断文件类型是否为音频
 */
const isAudioFile = (file: File): boolean => {
  return file.type.startsWith('audio/') || ACCEPT_AUDIO_MIMETYPE.includes(file.type);
};

/**
 * `accept` 项可为 MIME（如 application/x-subrip）或以 `.` 开头的扩展名（如 .srt）。
 * 扩展名匹配不依赖 `file.type`（部分环境对字幕等类型为空或非标准）。
 */
const matchesExtraFileAccept = (file: File, acceptTokens: string[]): boolean => {
  const nameLower = file.name.toLowerCase();

  for (const raw of acceptTokens) {
    const token = raw.trim();
    if (!token) continue;

    if (token.startsWith('.')) {
      if (nameLower.endsWith(token.toLowerCase())) {
        return true;
      }
      continue;
    }

    if (file.type && file.type === token) {
      return true;
    }
  }

  return false;
};

type MediaDropRejectMessageKey =
  | 'create.dragUpload.expectVideoOnly'
  | 'create.dragUpload.expectImageOnly'
  | 'create.dragUpload.unsupportedDropFileType';

type MediaUploadType = 'image' | 'video' | 'audio';

/**
 * 文件未进入上传队列时给出可读的提示（例如视频主体编辑区误拖入图片）。
 */
const getMediaDropRejectMessageKey = (
  file: File,
  acceptImage: boolean,
  acceptVideo: boolean,
  acceptAudio: boolean,
  videoAccept: string[],
): MediaDropRejectMessageKey => {
  if (!isVideoFile(file, videoAccept) && !acceptImage && acceptVideo && !acceptAudio) {
    return 'create.dragUpload.expectVideoOnly';
  }
  if (!isImageFile(file) && !acceptVideo && acceptImage && !acceptAudio) {
    return 'create.dragUpload.expectImageOnly';
  }
  return 'create.dragUpload.unsupportedDropFileType';
};

const getUploadLimitMessage = (
  t: ReturnType<typeof useTranslations>,
  type: MediaUploadType,
  remainingAcceptedTypes: MediaUploadType[],
): string => {
  const typeLabel = t(`create.dragUpload.typeLabel.${type}`);
  if (remainingAcceptedTypes.length === 0) {
    return t('create.dragUpload.limitReached', { type: typeLabel });
  }

  const remainingTypesLabel = remainingAcceptedTypes
    .map((itemType) => t(`create.dragUpload.typeLabel.${itemType}`))
    .join('/');

  return t('create.dragUpload.limitReachedWithRemainingTypes', {
    type: typeLabel,
    remainingTypes: remainingTypesLabel,
  });
};

export interface UseMediaUploadOptions {
  disabled?: boolean;
  // 总数限制（图片+视频+音频总数）
  maxCount?: number;
  // 图片上传相关配置
  maxImageCount?: number;
  maxImageFileSize?: number;
  isInitialSelectedImageRange?: boolean;
  getSelectedImage?: (url: string) => Promise<{ blob: Blob; width: number; height: number } | null>;
  validateImageResolution?: (imageResolution: { width: number; height: number }) => {
    errorMessage: string;
    errorReason: string;
  } | null;
  // 视频上传相关配置
  maxVideoCount?: number;
  maxVideoDuration?: number;
  minVideoDuration?: number;
  maxVideoSize?: number;
  validateVideoMetadata?: (metadata: {
    duration: number;
    aspectRatio: number;
    width: number;
    height: number;
  }) => string | null;
  scene?: UploadScene;
  videoScene?: UploadScene;
  videoAccept?: string[];
  captureCoverImage?: boolean;
  uploadCoverImage?: boolean;
  allowTempMode?: boolean; // 是否允许临时模式（未登录时不上传，只保存临时图片）
  emitUploadingInTempMode?: boolean; // 临时模式下是否仍然先发出 uploading 事件
  emitUploadingPreview?: boolean; // 图片/视频处理出本地预览后，上传完成前再发一次带 file 的 uploading 事件
  // 音频上传相关配置
  maxAudioCount?: number;
  maxAudioDuration?: number;
  minAudioDuration?: number;
  maxAudioSize?: number;
  /** 传给原生文件选择的 accept 字符串，同时作为音频校验规则；不传则使用内置默认 */
  audioAccept?: string;
  /** 是否接受图片文件，默认 true */
  acceptImage?: boolean;
  /** 是否接受视频文件，默认 true */
  acceptVideo?: boolean;
  /** 是否接受音频文件，默认 false */
  acceptAudio?: boolean;
  /**
   * 额外接受的文件类型（不走图片/视频/音频上传管线）。
   * 匹配 `accept` 时调用 `onFile`，不参与 `onChange`、batchIndex、`onBeforeBatchUpload` 的媒体列表。
   */
  extraFileAccept?: {
    accept: string[];
    onFile: (file: File) => void | Promise<void>;
  };
  /** 某类媒体已达数量上限时返回自定义提示 */
  getLimitReachedMessage?: (context: {
    type: MediaUploadType;
    remainingAcceptedTypes: MediaUploadType[];
  }) => string | null | undefined;
  /** 统一的上传状态变化回调 */
  onChange?: (
    file: UploadFile | AudioUploadFile,
    type: 'image' | 'video' | 'audio',
    info?: ImageUploadFileInfo | VideoUploadFileInfo | AudioUploadFileInfo,
  ) => void;
  /**
   * 媒体本地元数据（视频 / 音频时长，图片立即触发）解析完毕后回调。
   * 调用方可据此立即在 UI 上根据已知 duration 做加载占位，无需等到上传完成。
   */
  onMetadataReady?: (info: {
    id: string;
    type: 'image' | 'video' | 'audio';
    batchIndex?: number;
    durationSec: number;
    name?: string;
  }) => void;
  /**
   * 生命周期：本批 `File` 已通过 accept 并映射为 `{ file, type }[]` 之后、进入并行上传之前。
   * 可异步增删或重排本批任务；常用于同批多类型协同校验（例如封面图与视频先比对宽高比再决定是否上传图）。
   */
  onBeforeBatchUpload?: (
    items: Array<{ file: File; type: 'image' | 'video' | 'audio' }>,
  ) => Promise<Array<{ file: File; type: 'image' | 'video' | 'audio' }>>;
}

export interface UseMediaUploadResult {
  /**
   * 处理文件上传：根据文件类型自动路由到相应的上传逻辑
   * 支持多文件上传，图片和视频并行处理
   */
  handleFiles: (files: FileList | File[]) => Promise<void>;
  /**
   * 处理单个文件上传
   */
  handleFile: (file: File) => Promise<void>;
}

/**
 * 媒体上传 Hook
 *
 * 提供图片和视频上传的统一处理逻辑
 * 根据文件类型自动路由到相应的上传处理函数
 * 支持多文件上传，图片和视频并行处理
 *
 */
export const useMediaUpload = ({
  disabled,
  maxCount,
  maxImageCount,
  maxImageFileSize = MAX_IMAGE_FILE_SIZE,
  isInitialSelectedImageRange = true,
  getSelectedImage,
  validateImageResolution,
  maxVideoCount,
  maxVideoDuration,
  minVideoDuration,
  maxVideoSize = MAX_VIDEO_FILE_SIZE,
  validateVideoMetadata,
  scene = UPLOAD_SCENE.VIDU,
  videoScene = scene,
  videoAccept = ACCEPT_VIDEO_MIMETYPE,
  captureCoverImage = false,
  uploadCoverImage = false,
  allowTempMode = false,
  emitUploadingInTempMode = true,
  emitUploadingPreview = false,
  maxAudioCount,
  maxAudioDuration,
  minAudioDuration,
  maxAudioSize,
  audioAccept,
  acceptImage = true,
  acceptVideo = true,
  acceptAudio = false,
  extraFileAccept,
  getLimitReachedMessage,
  onChange,
  onMetadataReady,
  onBeforeBatchUpload,
}: UseMediaUploadOptions = {}): UseMediaUploadResult => {
  const t = useTranslations();

  // 图片验证
  const { validateImage } = useImageValidator({
    maxFileSize: maxImageFileSize,
    validateResolution: validateImageResolution,
  });

  // 图片上传处理
  // 图片没有时长概念：在收到第一次 `uploading` 事件时立即触发 onMetadataReady，
  // 由调用方决定占位时长（编辑器场景为 VIDEO_EDIT_IMAGE_CLIP_DEFAULT_SEC）
  const imageMetadataEmittedRef = useRef<Set<string>>(new Set());
  const { processFileUpload: processImageUpload } = useFileUpload({
    onChange: (file, info) => {
      if (file.status === 'uploading' && !imageMetadataEmittedRef.current.has(file.id)) {
        imageMetadataEmittedRef.current.add(file.id);
        onMetadataReady?.({ id: file.id, type: 'image', batchIndex: file.batchIndex, durationSec: 0 });
      }
      if (file.status !== 'uploading') {
        imageMetadataEmittedRef.current.delete(file.id);
      }
      onChange?.(file, 'image', info);
    },
    getSelectedImage,
    validateImage,
    isInitialSelectedImageRange,
    scene,
    allowTempMode,
    emitUploadingInTempMode,
    emitUploadingPreview,
  });

  // 视频上传处理
  const { uploadFile: uploadVideoFile } = useVideoUpload({
    maxDuration: maxVideoDuration,
    minDuration: minVideoDuration,
    maxSize: maxVideoSize,
    validateMetadata: validateVideoMetadata,
    scene: videoScene,
    videoAccept,
    captureCoverImage,
    uploadCoverImage,
    allowTempMode,
    emitUploadingInTempMode,
    emitUploadingPreview,
    onChange: (file, info) => onChange?.(file, 'video', info),
    onMetadataReady: (info) => onMetadataReady?.({ ...info, type: 'video' }),
  });

  // 音频上传处理
  const { uploadFile: uploadAudioFile } = useAudioUpload({
    maxDuration: maxAudioDuration,
    minDuration: minAudioDuration,
    maxSize: maxAudioSize,
    scene,
    allowTempMode,
    accept: audioAccept,
    onChange: (file, info) => onChange?.(file, 'audio', info),
    onMetadataReady: (info) => onMetadataReady?.({ ...info, type: 'audio' }),
  });

  /**
   * 处理单个文件上传
   */
  const handleFile = useCallback(
    async (file: File) => {
      if (disabled) return;

      if (extraFileAccept && matchesExtraFileAccept(file, extraFileAccept.accept)) {
        await extraFileAccept.onFile(file);
      } else if (acceptImage && isImageFile(file)) {
        if (onChange) {
          await processImageUpload(file, 0);
        }
      } else if (acceptVideo && isVideoFile(file, videoAccept)) {
        if (onChange) {
          await uploadVideoFile(file);
        }
      } else if (acceptAudio && isAudioFile(file)) {
        if (onChange) {
          await uploadAudioFile(file);
        }
      } else {
        toast({
          type: 'warning',
          message: t(getMediaDropRejectMessageKey(file, acceptImage, acceptVideo, acceptAudio, videoAccept)),
        });
      }
    },
    [
      disabled,
      extraFileAccept,
      acceptImage,
      acceptVideo,
      acceptAudio,
      onChange,
      processImageUpload,
      uploadVideoFile,
      uploadAudioFile,
      videoAccept,
      t,
    ],
  );

  /**
   * 处理文件上传：根据文件类型自动路由到相应的上传逻辑
   *
   * 上传策略：所有图片、视频和音频都并行上传，统一计算 batchIndex
   * 限制策略：
   * - 如果传了 maxCount，则以 maxCount 为最大总数限制（图片+视频+音频总数）
   * - maxImageCount、maxVideoCount、maxAudioCount 是分别控制的，选填
   * - 如果多个都填了，就是多个限制都分别要满足
   */
  const handleFiles = useCallback(
    async (files: FileList | File[]) => {
      if (disabled || !files || files.length === 0) return;

      const fileArray = Array.from(files);

      // 分离图片、视频、音频文件，保持原始顺序（仅收集 accept 允许的类型）
      const fileList: Array<{ file: File; type: 'image' | 'video' | 'audio' }> = [];
      const extraFiles: File[] = [];

      for (const file of fileArray) {
        if (extraFileAccept && matchesExtraFileAccept(file, extraFileAccept.accept)) {
          extraFiles.push(file);
          continue;
        }
        if (acceptImage && isImageFile(file)) {
          fileList.push({ file, type: 'image' });
        } else if (acceptVideo && isVideoFile(file, videoAccept)) {
          fileList.push({ file, type: 'video' });
        } else if (acceptAudio && isAudioFile(file)) {
          fileList.push({ file, type: 'audio' });
        }
      }

      if (fileList.length === 0 && extraFiles.length === 0 && fileArray.length > 0) {
        toast({
          type: 'warning',
          message: t(getMediaDropRejectMessageKey(fileArray[0], acceptImage, acceptVideo, acceptAudio, videoAccept)),
        });
        return;
      }

      if (extraFiles.length > 0 && extraFileAccept) {
        await Promise.all(extraFiles.map((file) => extraFileAccept.onFile(file)));
      }

      if (fileList.length === 0) {
        return;
      }

      let itemsToUpload = fileList;
      if (onBeforeBatchUpload) {
        itemsToUpload = await onBeforeBatchUpload(fileList);
      }

      // 收集所有上传任务，统一计算 batchIndex
      const uploadPromises: Promise<void>[] = [];
      let batchIndex = 0;
      let imageCount = 0;
      let videoCount = 0;
      let audioCount = 0;
      let totalCount = 0;
      let limitReachedMessage: string | null = null;

      const getRemainingAcceptedTypes = (currentType: MediaUploadType): MediaUploadType[] => {
        const remainingTypes: MediaUploadType[] = [];

        if (acceptImage && currentType !== 'image' && (maxImageCount === undefined || imageCount < maxImageCount)) {
          remainingTypes.push('image');
        }
        if (acceptVideo && currentType !== 'video' && (maxVideoCount === undefined || videoCount < maxVideoCount)) {
          remainingTypes.push('video');
        }
        if (acceptAudio && currentType !== 'audio' && (maxAudioCount === undefined || audioCount < maxAudioCount)) {
          remainingTypes.push('audio');
        }

        return remainingTypes;
      };

      const setLimitReachedMessage = (type: MediaUploadType) => {
        if (limitReachedMessage) {
          return;
        }

        const remainingAcceptedTypes = getRemainingAcceptedTypes(type);
        limitReachedMessage =
          getLimitReachedMessage?.({ type, remainingAcceptedTypes }) ??
          getUploadLimitMessage(t, type, remainingAcceptedTypes);
      };

      for (const item of itemsToUpload) {
        // 检查总数限制（如果设置了 maxCount）
        if (maxCount !== undefined && totalCount >= maxCount) {
          break;
        }

        if (item.type === 'image') {
          // 检查图片数量限制（如果设置了 maxImageCount）
          if (maxImageCount !== undefined && imageCount >= maxImageCount) {
            setLimitReachedMessage('image');
            continue;
          }
          if (onChange) {
            uploadPromises.push(processImageUpload(item.file, batchIndex));
          }
          imageCount++;
          totalCount++;
          batchIndex++;
        } else if (item.type === 'video') {
          // 检查视频数量限制（如果设置了 maxVideoCount）
          if (maxVideoCount !== undefined && videoCount >= maxVideoCount) {
            setLimitReachedMessage('video');
            continue;
          }
          if (onChange) {
            uploadPromises.push(uploadVideoFile(item.file, batchIndex));
          }
          videoCount++;
          totalCount++;
          batchIndex++;
        } else if (item.type === 'audio') {
          // 检查音频数量限制（如果设置了 maxAudioCount）
          if (maxAudioCount !== undefined && audioCount >= maxAudioCount) {
            setLimitReachedMessage('audio');
            continue;
          }
          if (onChange) {
            uploadPromises.push(uploadAudioFile(item.file));
          }
          audioCount++;
          totalCount++;
          batchIndex++;
        }
      }

      if (limitReachedMessage) {
        toast({
          type: 'warning',
          message: limitReachedMessage,
        });
      }

      // 并行执行所有上传任务
      await Promise.all(uploadPromises);
    },
    [
      disabled,
      extraFileAccept,
      acceptImage,
      acceptVideo,
      acceptAudio,
      maxCount,
      maxImageCount,
      maxVideoCount,
      maxAudioCount,
      onChange,
      processImageUpload,
      uploadVideoFile,
      uploadAudioFile,
      videoAccept,
      getLimitReachedMessage,
      onBeforeBatchUpload,
      t,
    ],
  );

  return {
    handleFiles,
    handleFile,
  };
};
